import { useEffect, useState } from "react";

export type AiMode = "read" | "read_add" | "full";

export const AI_MODE_OPTIONS: Array<{
  value: AiMode;
  label: string;
  description: string;
  danger?: boolean;
}> = [
  {
    value: "read",
    label: "Read only",
    description: "The assistant can read your Vouch data and answer questions, but never changes anything.",
  },
  {
    value: "read_add",
    label: "Read + add",
    description: "The assistant can also add new income, expenses, transfers, and loans on your behalf.",
  },
  {
    value: "full",
    label: "Full control",
    description: "The assistant can read, add, edit, and delete entries. Use with care.",
    danger: true,
  },
];

const AI_MODE_KEY = "vouch_ai_mode";
const AI_MODES = new Set<AiMode>(["read", "read_add", "full"]);

export function readAiMode(): AiMode {
  const value = localStorage.getItem(AI_MODE_KEY);
  return AI_MODES.has(value as AiMode) ? (value as AiMode) : "read";
}

export function saveAiMode(mode: AiMode): void {
  localStorage.setItem(AI_MODE_KEY, mode);
  window.dispatchEvent(new CustomEvent("vouch-ai-mode-change", { detail: mode }));
}

export function useAiModePreference(): [AiMode, (mode: AiMode) => void] {
  const [mode, setModeState] = useState<AiMode>(() => readAiMode());

  useEffect(() => {
    const onStorage = (event: StorageEvent): void => {
      if (event.key === AI_MODE_KEY) setModeState(readAiMode());
    };
    const onLocalChange = (): void => setModeState(readAiMode());

    window.addEventListener("storage", onStorage);
    window.addEventListener("vouch-ai-mode-change", onLocalChange);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("vouch-ai-mode-change", onLocalChange);
    };
  }, []);

  const setMode = (next: AiMode): void => {
    saveAiMode(next);
    setModeState(next);
  };

  return [mode, setMode];
}
